/**
 * Call the Vault routes that understand Kuali Research award semantics.
 *
 * Latest-ACTIVE replacement and stored system credentials are Kuali-specific.
 * Durable settings and explicit pull batches remain separate API boundaries.
 */
import type { NodeApiClient } from '@/app-shell/api/node-api-client'
import { connectorPath } from '../../api'
import type {
  ConnectorCredentialSource,
  ConnectorFeedMutation,
  ConnectorInitialSync,
  ConnectorSchedule,
} from '../../types'
import type {
  KualiResearchConnectionStatus,
  KualiResearchFeedOptions,
  KualiResearchFilterField,
  KualiResearchModuleOptions,
  KualiResearchSelectionPreview,
  KualiResearchSourceType,
  KualiResearchScope,
} from '../types'

export interface CreateKualiResearchFeedRequest {
  initialSync: ConnectorInitialSync
  schedule: ConnectorSchedule
  sourceDisplayFieldKeys: string[]
  kualiResearch: KualiResearchFeedOptions
}

export const createKualiResearchFeed = async (
  api: NodeApiClient,
  orgId: string,
  vaultId: string,
  request: CreateKualiResearchFeedRequest,
) => (await api.post<ConnectorFeedMutation>(
  `${connectorPath(orgId, vaultId)}/feeds`,
  {
    provider: 'kuali-research',
    direction: 'inbound',
    remoteFolderId: 'research',
    remoteFolderName: 'Kuali Research',
    ...request,
  },
)).data

export const updateKualiResearchFeed = async (
  api: NodeApiClient,
  orgId: string,
  vaultId: string,
  feedId: string,
  schedule: ConnectorSchedule,
  sourceDisplayFieldKeys: string[],
  kualiResearch: KualiResearchFeedOptions,
) => (await api.put<ConnectorFeedMutation>(
  `${connectorPath(orgId, vaultId)}/feeds/${encodeURIComponent(feedId)}/kuali-research`,
  {
    schedule,
    sourceDisplayFieldKeys,
    kualiResearch,
  },
)).data

export const getKualiResearchConnection = async (
  api: NodeApiClient,
  orgId: string,
  vaultId: string,
) => (await api.get<KualiResearchConnectionStatus>(
  `${connectorPath(orgId, vaultId)}/kuali-research/connection`,
)).data

export const saveKualiResearchConnection = async (
  api: NodeApiClient,
  orgId: string,
  vaultId: string,
  apiKey: string,
) => api.put(
  `${connectorPath(orgId, vaultId)}/kuali-research/connection`,
  { apiKey },
)

export const deleteKualiResearchConnection = async (
  api: NodeApiClient,
  orgId: string,
  vaultId: string,
) => api.delete(`${connectorPath(orgId, vaultId)}/kuali-research/connection`)

export const getKualiResearchFilterFields = async (
  api: NodeApiClient,
  orgId: string,
  vaultId: string,
  apiUrl: string,
  source: KualiResearchSourceType,
  scope: KualiResearchScope,
  credentialSource: ConnectorCredentialSource,
) => (await api.post<{ filterFields: KualiResearchFilterField[] }>(
  `${connectorPath(orgId, vaultId)}/kuali-research/filter-fields`,
  { apiUrl, source, scope, credentialSource },
)).data.filterFields

export const getKualiResearchSelectionPreview = async (
  api: NodeApiClient,
  orgId: string,
  vaultId: string,
  request: {
    apiUrl: string
    module: KualiResearchModuleOptions
    credentialSource: ConnectorCredentialSource
  },
) => (await api.post<KualiResearchSelectionPreview>(
  `${connectorPath(orgId, vaultId)}/kuali-research/selection-preview`,
  request,
)).data
